import { MongoAbility, RawRuleOf } from '@casl/ability'
import { BoundCanProps, useAbility as useBaseAbility } from '@casl/react'
import { Auth0TokenRoleKey } from '@eigtech/auth0-types'
import { getLog } from '@eigtech/ui-shared-logging'
import { jwtDecode } from 'jwt-decode'
import {
  ComponentType,
  Context,
  FC,
  FunctionComponent,
  useContext,
  useEffect,
  useState,
} from 'react'
import { useAuthStore } from '../store/useAuthStore'
import {
  Auth0AccessTokenJwtPayload,
  Permissions,
  Role,
  WithRequiredPermissionsProps,
} from './types/Permissions'
import {
  AbilityContext as BaseAbilityContext,
  Can as CanBase,
  ability as baseAbility,
} from './utils/context'
import { updateAbility as baseUpdateAbility } from './utils/updateAbility'
import {
  UseHasRequiredPermissionsProps,
  useHasRequiredPermissions as useHasRequiredPermissionsBase,
} from './utils/useHasRequiredPermissions'
import { withRequiredPermissions as withRequiredPermissionsBase } from './utils/withRequiredPermissions'

const log = getLog()

export type PermissionGenerator<Action extends string, Subject extends string> = (
  basePermissions: Permissions<Action, Subject>,
  roles: Role[]
) => RawRuleOf<MongoAbility<[Action, Subject]>>[]

export type PermissionsFactoryProps<Action extends string, Subject extends string> = {
  additionalPermissionsGenerators?: PermissionGenerator<Action, Subject>[]
  ForbiddenComponent: FC
}

export function permissionsFactory<Action extends string, Subject extends string>({
  additionalPermissionsGenerators = [],
  ForbiddenComponent,
}: PermissionsFactoryProps<Action, Subject>) {
  type AbilityType = MongoAbility<[Action, Subject]>

  const ability = baseAbility as unknown as AbilityType

  const AbilityContext = BaseAbilityContext as unknown as Context<AbilityType>

  const useAbilityContext = () => useContext(AbilityContext)

  const useAbility = () => useBaseAbility(AbilityContext)

  const updateAbility = (permissions: Permissions<Action, Subject>, roles: Role[]) =>
    baseUpdateAbility(permissions, roles, additionalPermissionsGenerators)

  function useKeepAbilityUpToDate() {
    const [hasUpdatedAbility, setHasUpdatedAbility] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const [isPending, setIsPending] = useState(true)

    const hasSetAccessToken = useAuthStore((state) => state.hasSetAccessToken)
    const getAccessToken = useAuthStore((state) => state.getAccessToken)

    useEffect(() => {
      if (!hasSetAccessToken) return

      async function handleUpdatePermissions() {
        setIsLoading(true)

        try {
          const accessToken = await getAccessToken()

          if (accessToken) {
            const decodedToken = jwtDecode<Auth0AccessTokenJwtPayload<Action, Subject>>(accessToken)
            updateAbility(decodedToken.permissions, decodedToken[Auth0TokenRoleKey])
            setHasUpdatedAbility(true)
          }
        } catch (error) {
          // getAccessToken can reject (e.g. offline token refresh) and jwtDecode can throw
          // on a malformed token. Either way we must still resolve the pending state below,
          // otherwise the consuming guard hangs on a "Getting permissions..." spinner forever.
          log.error('failed to update permissions from access token', { error })
        } finally {
          setIsLoading(false)
          setIsPending(false)
        }
      }

      handleUpdatePermissions()
    }, [getAccessToken, hasSetAccessToken])

    return { hasUpdatedAbility, isLoading, isPending }
  }

  const Can = CanBase as unknown as FunctionComponent<BoundCanProps<AbilityType>>

  const useHasRequiredPermissions = (props: UseHasRequiredPermissionsProps<Action, Subject>) =>
    useHasRequiredPermissionsBase(props)

  const withRequiredPermissions = <Props extends object = object>(
    Component: ComponentType<Props>,
    props: WithRequiredPermissionsProps<Action, Subject> & { Forbidden?: FC }
  ) => withRequiredPermissionsBase(Component, { Forbidden: ForbiddenComponent, ...props })

  return {
    ability,
    AbilityContext,
    useAbility,
    useAbilityContext,
    updateAbility,
    useKeepAbilityUpToDate,
    Can,
    useHasRequiredPermissions,
    withRequiredPermissions,
  }
}
